import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type {
  OrgInsights,
  ProgramInsightsTopline,
} from "../orgInsights.types";

/**
 * Sending Institution Insights are published as static JSON by the
 * `export_program_insights` Django management command and served from
 * `/static/insights/programs/<program_id>/`. They have no auth (per the
 * "guessable URLs are fine" decision for the Friday ship), so we fetch
 * them with the raw browser `fetch` instead of the authenticated
 * `apiRequest` helper.
 */
const INSIGHTS_BASE_PATH = "/static/insights/programs";

function buildToplineUrl(programId: number): string {
  return `${INSIGHTS_BASE_PATH}/${programId}/_topline.json`;
}

function buildOrgUrl(programId: number, organizationId: number): string {
  return `${INSIGHTS_BASE_PATH}/${programId}/orgs/${organizationId}.json`;
}

/**
 * Fetch the program-wide insights topline.
 *
 * Resolves to `null` if the program has no published insights file yet
 * (i.e. the publisher hasn't run for this program). The container treats
 * `null` as "hide the section entirely" rather than as an error.
 */
export function useFetchProgramInsightsTopline(
  programId: number | null
): UseQueryResult<ProgramInsightsTopline | null> {
  return useQuery({
    queryKey: ["program-insights", "topline", programId],
    enabled: programId != null,
    queryFn: async (): Promise<ProgramInsightsTopline | null> => {
      const res = await fetch(buildToplineUrl(programId as number));
      if (res.status === 404) return null;
      if (!res.ok) {
        throw new Error(
          `Failed to fetch program insights topline (HTTP ${res.status})`
        );
      }
      return (await res.json()) as ProgramInsightsTopline;
    },
    // Insights refresh on a weekly cadence at most. Keep them in cache
    // until the page is closed; no point refetching on focus.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/**
 * Fetch a single org's insights slice.
 *
 * Resolves to `null` when the org doesn't have a published file — that
 * happens for orgs with fewer than 3 onboarded participants (the probe's
 * statistical-noise threshold) and for orgs not in the program at all.
 * The container shows the "below threshold" message in both cases.
 */
export function useFetchOrgInsights(
  programId: number | null,
  organizationId: number | null
): UseQueryResult<OrgInsights | null> {
  return useQuery({
    queryKey: ["program-insights", "org", programId, organizationId],
    enabled: programId != null && organizationId != null,
    queryFn: async (): Promise<OrgInsights | null> => {
      const res = await fetch(
        buildOrgUrl(programId as number, organizationId as number)
      );
      if (res.status === 404) return null;
      if (!res.ok) {
        throw new Error(
          `Failed to fetch org insights (HTTP ${res.status})`
        );
      }
      return (await res.json()) as OrgInsights;
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}
